/**
 * Parameter schema shared by visualizations, post-effects, settings and the modulation matrix.
 * UI controls are generated from these definitions; nothing is hand-wired per visualization.
 */

export interface FloatParam {
  id: string;
  label: string;
  type: 'float';
  min: number;
  max: number;
  default: number;
  step?: number;
  description?: string;
  /** UI hint: render as a toggle (0/1) or a select over `options` (value = index). */
  control?: 'slider' | 'toggle' | 'select';
  options?: string[];
  /** The adaptive governor may lower this parameter to save GPU time. */
  quality?: boolean;
}

export type ParamDef = FloatParam;

export type ParamValues = Record<string, number>;

/** Maximum parameters per visualization; they map onto the uP[] uniform array in order. */
export const MAX_VIZ_PARAMS = 8;

export function defaultParamValues(defs: readonly ParamDef[]): ParamValues {
  const out: ParamValues = {};
  for (const d of defs) out[d.id] = d.default;
  return out;
}

export function clampParam(def: ParamDef, value: number): number {
  if (!Number.isFinite(value)) return def.default;
  const v = Math.min(def.max, Math.max(def.min, value));
  return def.step && def.step >= 1 ? Math.round(v) : v;
}

export function toggle(id: string, label: string, def = 0): ParamDef {
  return { id, label, type: 'float', min: 0, max: 1, default: def, step: 1, control: 'toggle' };
}
